import { Country } from 'src/common/types';
import { formatMessage } from 'src/core/services/http.service';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import i18n, { Languages } from '../i18n';
import FileSaver from 'file-saver';
import { ThemeSelection } from 'src/common/themes';
import { GroupType } from './groups-store';
import { DeviceType } from './licensing-store';
import { validateEmail } from 'src/core/utils/object';

export interface UserOrganizationMemberItem {
  id: number;
  organizationId: string;
  organizationDisplayName: string;
}

export interface UserGroupItem {
  groupId: string;
  name: string;
  organizationId: string;
  organizationName: string;
  groupType?: GroupType;
}

export interface UserSummary {
  id: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  createdOn: Date;
  modifiedOn: Date;
  groups: UserGroupItem[];
  organizations: UserOrganizationMemberItem[];
  resendInvitation: boolean;
  country: Country;
}

export interface UserItem {
  id: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  createdOn: Date;
  modifiedOn: Date;
  groups: UserGroupItem[];
  organizations: UserOrganizationMemberItem[];
  defaultLanguage: string;
  country: Country;
  twoFactorEnabled?: boolean;
}

export interface NewUserItem {
  email: string;
  displayName: string;
  password: string;
  organizationId: string;
  sendInvitation: boolean;
  defaultLanguage?: string;
}

export interface EditUserItem {
  displayName: string;
  defaultLanguage?: string;
}

export enum UserSharingDataEmailFrequency {
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quaterly = 'Quaterly',
}

export interface UserSharingDataSettingsItem {
  id?: string;
  frequency: UserSharingDataEmailFrequency;
  EmailAddress: string[];
}

export enum CsvReportKeys {
  Organization,
  Result,
  Organizations,
  User,
  Email,
  Country,
  UsersNumber,
  ProjectsNumber,
  SurveysNumber,
  Projects,
  Surveys,
  Users,
  CreationDate,
  LastUpdate,
  PartnerOrganization,
  DisplayName,
  FromDate,
  UntilDate,
  CreatedBy,
  ActiveDays,
  LicenseName,
  OrganizationName,
  Status,
  ActiveUser,
  SoftwareVersion,
  LogType,
  CreatedOn,
  Description,
  UserName,
  ProjectName,
  ToolType,
  ToolSerialNumber,
  FirstUsedOn,
  LastUsedOn,
  MeasurementCount,
}

type UsersCsvReportRequest = {
  fileName: string;
  csvSeparator: string;
  headers: { key: CsvReportKeys; translatedHeader: string }[];
  currentLanguage: string;
};

export interface ShareLicenseDataRequest {
  users: string[];
  licenseData: string;
  licenseQR: string;
  toolCode: string;
  deviceType: DeviceType;
  fromDate?: Date;
  untilDate?: Date;
  workinHours?: number;
  unlimited: boolean;
}

export interface LicenseCreationEmailRequest {
  toolNumber: string;
  deviceType: string;
}

export class UserSummaryStore extends DataStore<UserSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/users`, []);
  }

  public async shareLicenseData(item: ShareLicenseDataRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<ShareLicenseDataRequest, CommandResult<ShareLicenseDataRequest>>(
        `${this.baseUrl}/share-license`,
        item
      );
      return result.data;
    });
  }

  public async sendLicenseCreationEmail(item: LicenseCreationEmailRequest) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<LicenseCreationEmailRequest, CommandResult<LicenseCreationEmailRequest>>(
        `${this.baseUrl}/license-signoff-email`,
        item
      );
      return result.data;
    });
  }

  public async getUpdatedUserIds(userIdsOrEmails: string[]) {
    return await this.handleCallAsync(async () => {
      const emails = userIdsOrEmails.filter((v) => validateEmail(v));
      const emailFilter = emails.map((email) => `Email eq '${email}'`).join(' or ');
      var query: Partial<Query> = {
        filter: [emailFilter],
        take: 1000000,
      };

      let items: UserSummary[] = [];

      if (emails.length > 0) {
        var response = await this.httpService.get<QueryResult<UserSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
        items = response.data.items;
      }

      let mappedUsers: Record<string, string> = {};

      for (let index = 0; index < userIdsOrEmails.length; index++) {
        const idOrEmail = userIdsOrEmails[index];
        const newUser = items.find((user) => user.email === idOrEmail);
        if (newUser) {
          mappedUsers[idOrEmail] = newUser.id;
        } else {
          mappedUsers[idOrEmail] = idOrEmail;
        }
      }

      return mappedUsers;
    });
  }

  public async exportCsv(query: Query, separator: string, fileName: string) {
    const item: UsersCsvReportRequest = {
      fileName,
      csvSeparator: separator,
      headers: [
        { key: CsvReportKeys.Email, translatedHeader: i18n.t('Email') },
        { key: CsvReportKeys.DisplayName, translatedHeader: i18n.t('DisplayName') },
        { key: CsvReportKeys.Organization, translatedHeader: i18n.t('Organizations') },
      ],
      currentLanguage: i18n.language !== Languages.Russian ? i18n.language : 'ru-RU',
    };
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<UsersCsvReportRequest, Blob>(`${this.baseUrl}/get-csv-report?${DataStore.buildUrl(query)}`, item, {
        responseType: 'blob',
      });

      if (result.data == null || result.data.size === 0) {
        throw Error(i18n.t('CrashError'));
      }

      FileSaver.saveAs(result.data, item.fileName);

      return true;
    });
  }

  public async delete(id: string, params?: any) {
    return await (async () => {
      try {
        this._state.set((s) => {
          s.errorMessage = undefined;
          s.isBusy = true;
          return s;
        });
        const response = await this.httpService.delete<any, CommandResult<any>>(`${this.baseUrl}/${encodeURIComponent(id)}`, params);
        return response.data;
      } catch (ex) {
        const msg = formatMessage(ex);
        this._state.set((s) => {
          s.errorMessage = msg;
          return s;
        });
        return { isSuccess: false, error: msg, messages: [] };
      } finally {
        this._state.set((s) => {
          s.isBusy = false;
          return s;
        });
      }
    })();
  }
  sendResetPasswordEmailPath = 'send-reset-password-email';
  public async sendResetPasswordEmail(id: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{}, CommandResult<{}>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.sendResetPasswordEmailPath}`,
        {}
      );
      return result.data;
    });
  }

  public async resetLockout(id: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{}, CommandResult<{}>>(`${this.baseUrl}/${encodeURIComponent(id)}/reset-lockout`, {});
      return result.data;
    });
  }
}

export class UserProfileItemStore extends FormStore<UserItem, {}> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/users`);
  }
}

export class UserItemStore extends FormStore<UserItem, NewUserItem | EditUserItem> {
  host: string;
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/users`);
    this.host = baseUrl;
  }

  userGroupsBasePath = 'groups';
  userOrganizationsMembershipBasePath = 'organizations-membership';
  sendInvitationPath = 'send-invitation';
  sendResetPasswordEmailPath = 'send-reset-password-email';
  public async getUserItem(id: string, groupTypes: GroupType[]) {
    return await this.handleCallAsync(async () => {
      const url = `${this.baseUrl}/${encodeURIComponent(id)}?${groupTypes.map((g) => `GroupTypes=${g}`).join('&')}`;
      var response = await this.httpService.get<UserItem>(url, {
        IncludeSharingUsers: false,
      });
      this._state.set((s) => {
        s.item = response.data as UserItem;
        return s;
      });
      return true;
    });
  }
  public async addUserGroups(id: string, groupIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ groups: string[] }, CommandResult<{ groups: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.userGroupsBasePath}`,
        { groups: groupIds }
      );
      return result.data;
    });
  }

  public async deleteUserGroups(id: string, groupIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{ groups: string[] }, CommandResult<{ groups: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.userGroupsBasePath}`,
        { groups: groupIds }
      );
      return result.data;
    });
  }

  public async sendInvitation(id: string, orgId: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.sendInvitationPath}/${encodeURIComponent(orgId)}`,
        {}
      );
      return result.data;
    });
  }

  public async switchMFA(id: string, status: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.patch<{ twoFactorEnabled: boolean }, CommandResult<{ twoFactorEnabled: boolean }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/two-factor`,
        {
          twoFactorEnabled: status,
        }
      );
      return result.data;
    });
  }

  public async sendResetPasswordEmail(id: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{}, CommandResult<{}>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.sendResetPasswordEmailPath}`,
        {}
      );
      return result.data;
    });
  }

  public async updatePreferredTheme(id: string, theme: ThemeSelection) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ theme: ThemeSelection }, void>(`${this.baseUrl}/${encodeURIComponent(id)}/update-preferred-theme`, {
        theme,
      });
      return result.data;
    });
  }
}

export class UserSharingDataSettingsStore extends FormStore<UserSharingDataSettingsItem, UserSharingDataSettingsItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/email-job-config`);
  }

  public async loadSettings() {
    return await this.handleCallAsync(async () => {
      let response = await this.httpService.get<any>(`${this.baseUrl}/get-user-data-sharing-config`);
      if (response.data) {
        const item: UserSharingDataSettingsItem = {
          frequency: response.data.frequency,
          EmailAddress: response.data.emails ? response.data.emails.split(';') : [],
          id: response.data.id,
        };
        this._state.set((s) => {
          s.item = item;
          return s;
        });
      } else {
        this._state.set((s) => {
          s.item = { EmailAddress: [], frequency: UserSharingDataEmailFrequency.Weekly };
          return s;
        });
      }

      return true;
    });
  }

  public async updateSettings(item: UserSharingDataSettingsItem, id?: string) {
    return await this.handleCallAsync(async () => {
      let result;
      if (id) {
        result = await this.httpService.put(`${this.baseUrl}/${id}`, item);
      } else {
        result = await this.httpService.post(`${this.baseUrl}/create-user-data-sharing-config`, item);
      }
      return result.data;
    });
  }
}
